import React, { FunctionComponent, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import SearchIcon from '@mui/icons-material/Search';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import {
  Box,
  Chip,
  Grid
} from '@mui/material';

import {
  TextInput,
  Typography
} from './styled';

type Props = {
  search: string;
  setSearch: (s:string) => void;
  objectCount: number;
  tags?: {id: string; name: string;}[];
  tags2?: {id: string; name: string;}[];
  multiTags?: {id: number; name: string; cnt?: number;}[];
  activeTag?: string;
  activeTag2?: string;
  activeTags?: number[];
  setActiveTag?: (s:string) => void;
  setActiveTag2?: (s:string) => void;
  setActiveTags?: (s:number[]) => void;
  term?: string;
  hideableTags?: boolean;
};

export const SearchBox: FunctionComponent<Props> = ({term, hideableTags, search, setSearch, objectCount, tags, activeTag, setActiveTag, tags2, activeTag2, setActiveTag2, activeTags, setActiveTags, multiTags}) => {
  const theme = useTheme();
  const {t, i18n} = useTranslation(['translations']);

  const [tagsHidden, setTagsHidden] = useState<boolean>(!!hideableTags);

  return (<Box sx={{
    marginTop: 2,
    marginBottom: 1,
    padding: 1,
    border: '1px solid transparent',
    borderColor: theme.palette.primary.main,
    backgroundImage: `linear-gradient(to right, transparent, ${theme.palette.primary.main})`,
    borderRadius: 2
  }}>
    <Grid container spacing={1}>
      <Grid item xs={12} md={6} sx={{
        paddingLeft: {
          xs:  "0px !important",
          md: "16px !important"
        },
        textAlign: {
          xs: 'center',
          md: 'left'
        }
      }}>
        <Typography sx={{
          lineHeight: {
            xs: 1.5,
            md: '50px'
          }
        }}>{objectCount} {!!term ? term : t("general.objects_found")}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          id="search"
          noCaption
          placeholder={t("shared.terms.search_field")}
          value={search}
          endIcon={<SearchIcon />}
          onChange={(e) => {
            setSearch(e);
          }}
          sx={{marginTop: 0}}
        />
      </Grid>
      {!!multiTags && <Grid item xs={12} sx={{textAlign: 'right'}}>
        {(!!hideableTags && tagsHidden ? multiTags.sort((a, b) => (b.cnt || 0) - (a.cnt || 0)).slice(0, 5) : multiTags.sort((a, b) => ('' + a.name).localeCompare(b.name))).map(tag => <Chip
          label={t(tag.name)}
          variant={(activeTags || []).indexOf(tag.id) > -1 ? "filled" : "outlined"}
          onDelete={(activeTags || []).indexOf(tag.id) > -1 ? () => {
            if (!!setActiveTags && (activeTags || []).indexOf(tag.id) < 0) setActiveTags([...(activeTags || []), tag.id]);
            if (!!setActiveTags && (activeTags || []).indexOf(tag.id) > -1) setActiveTags((activeTags || []).filter(fff => fff !== tag.id));
          } : undefined}
          onClick={() => {
            if (!!setActiveTags && (activeTags || []).indexOf(tag.id) < 0) setActiveTags([...(activeTags || []), tag.id]);
            if (!!setActiveTags && (activeTags || []).indexOf(tag.id) > -1) setActiveTags((activeTags || []).filter(fff => fff !== tag.id));
          }}
          sx={{
            borderColor: 'white',
            marginLeft: 0.5,
            ...((activeTags || []).indexOf(tag.id) > -1 ? {
              backgroundColor: theme.palette.tertiary?.main,
              "&:hover": {
                backgroundColor: theme.palette.tertiary?.main,
              }
            } : {})
          }}
        />)}
        {!!hideableTags && !!tagsHidden && <Chip sx={{marginLeft: 0.5}} label={t("shared.chips.show_more_tags")} icon={<Visibility />} onClick={() => {setTagsHidden(false);}} />}
        {!!hideableTags && !tagsHidden && <Chip sx={{marginLeft: 0.5}} label={t("shared.chips.show_less_tags")} icon={<VisibilityOff />} onClick={() => {setTagsHidden(true);}} />}
      </Grid>}
      {!!tags && <Grid item xs={12} sx={{textAlign: 'right'}}>
        {(!!hideableTags && tagsHidden ? tags.sort((a, b) => ('' + a.name).localeCompare(b.name)).slice(0, 5) : tags.sort((a, b) => ('' + a.name).localeCompare(b.name))).map(tag => <Chip
          label={t(tag.name)}
          variant={tag.id === activeTag ? "filled" : "outlined"}
          onDelete={tag.id === activeTag ? () => {
            if (!!setActiveTag && tag.id !== activeTag) setActiveTag(tag.id);
            if (!!setActiveTag && tag.id === activeTag) setActiveTag('');
          } : undefined}
          onClick={() => {
            if (!!setActiveTag && tag.id !== activeTag) setActiveTag(tag.id);
            if (!!setActiveTag && tag.id === activeTag) setActiveTag('');
          }}
          sx={{
            borderColor: 'white',
            marginLeft: 0.5,
            ...(tag.id === activeTag ? {
              backgroundColor: theme.palette.tertiary?.main,
              "&:hover": {
                backgroundColor: theme.palette.tertiary?.main,
              }
            } : {})
          }}
        />)}
        {!!hideableTags && !!tagsHidden && <Chip sx={{marginLeft: 0.5}} label={t("shared.chips.show_more_tags")} icon={<Visibility />} onClick={() => {setTagsHidden(false);}} />}
        {!!hideableTags && !tagsHidden && <Chip sx={{marginLeft: 0.5}} label={t("shared.chips.show_less_tags")} icon={<VisibilityOff />} onClick={() => {setTagsHidden(true);}} />}
      </Grid>}
      {!!tags2 && <Grid item xs={12} sx={{textAlign: 'right'}}>
        {(!!hideableTags && tagsHidden ? tags2.slice(0, 5) : tags2).map(tag => <Chip
          label={t(tag.name)}
          variant={tag.id === activeTag2 ? "filled" : "outlined"}
          onDelete={tag.id === activeTag2 ? () => {
            if (!!setActiveTag2 && tag.id !== activeTag2) setActiveTag2(tag.id);
            if (!!setActiveTag2 && tag.id === activeTag2) setActiveTag2('');
          } : undefined}
          onClick={() => {
            if (!!setActiveTag2 && tag.id !== activeTag2) setActiveTag2(tag.id);
            if (!!setActiveTag2 && tag.id === activeTag2) setActiveTag2('');
          }}
          sx={{
            borderColor: 'white',
            marginLeft: 0.5,
            ...(tag.id === activeTag2 ? {
              backgroundColor: theme.palette.tertiary?.main,
              "&:hover": {
                backgroundColor: theme.palette.tertiary?.main,
              }
            } : {})
          }}
        />)}
        {!!hideableTags && !!tagsHidden && <Chip sx={{marginLeft: 0.5}} label={t("shared.chips.show_more_tags")} icon={<Visibility />} onClick={() => {setTagsHidden(false);}} />}
        {!!hideableTags && !tagsHidden && <Chip sx={{marginLeft: 0.5}} label={t("shared.chips.show_less_tags")} icon={<VisibilityOff />} onClick={() => {setTagsHidden(true);}} />}
      </Grid>}
    </Grid>
  </Box>);
}